import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wallet, Phone, MessageSquare, Bot, ChevronDown, Plus, IndianRupee, Info, Smartphone } from 'lucide-react';
import api from '../../../api/axios';
import { logout } from '../../../auth/auth';

const formatCurrency = (amount) => {
  if (amount == null) return '0.00';
  return Number(amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const USAGE_ITEMS = [
  { key: 'aiCalls', label: 'AI Calls', icon: Bot, color: 'text-blue-600', bg: 'bg-blue-50' },
  { key: 'phoneRental', label: 'Phone Rental', icon: Phone, color: 'text-violet-600', bg: 'bg-violet-50' },
  { key: 'messages', label: 'Messages', icon: MessageSquare, color: 'text-emerald-600', bg: 'bg-emerald-50' },
];

const MinutesBalanceWidget = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const fetchBalance = async () => {
      try {
        const response = await api.get('/api/v1/wallet/balance');

        if (!response.data?.success) {
          throw new Error(response.data?.message || 'Operation failed');
        }

        setData(response.data.data);
      } catch (err) {
        if (err.response) {
          if (err.response.status === 401) {
            logout();
            return;
          }
          if (err.response.status === 403) {
            setError('Access denied.');
            return;
          }
          if (err.response.status >= 500) {
            setError('Server error.');
            return;
          }
        }
        setError(err.message || 'Failed to fetch balance.');
      } finally {
        setLoading(false);
      }
    };

    fetchBalance();
  }, []);

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-4 min-w-[260px]">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-gray-100 animate-pulse" />
          <div className="space-y-1.5 flex-1">
            <div className="h-3 w-16 bg-gray-100 rounded animate-pulse" />
            <div className="h-5 w-24 bg-gray-100 rounded animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-4 min-w-[260px]">
        <div className="flex items-center gap-2 text-sm text-red-600">
          <Wallet className="w-4 h-4" />
          {error}
        </div>
      </div>
    );
  }

  const balance = data?.balance ?? 0;
  const usage = {
    aiCalls: data?.usageBreakdown?.aiCalls ?? 0,
    phoneRental: data?.usageBreakdown?.phoneRental ?? 0,
    messages: data?.usageBreakdown?.messages ?? 0,
  };
  const totalSpent = Object.values(usage).reduce((sum, v) => sum + v, 0);
  const isLow = balance < 500;

  return (
    <div className="relative">
      {/* Compact trigger */}
      <button
        onClick={() => setExpanded((prev) => !prev)}
        className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border transition-all hover:shadow-md ${
          isLow
            ? 'bg-red-50 border-red-200 hover:border-red-300'
            : 'bg-white border-gray-200 hover:border-gray-300'
        }`}
      >
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isLow ? 'bg-red-100' : 'bg-blue-50'}`}>
          <Wallet className={`w-4 h-4 ${isLow ? 'text-red-600' : 'text-blue-600'}`} />
        </div>
        <div className="text-left">
          <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-400 leading-none mb-1">Balance</p>
          <div className="flex items-center gap-0.5">
            <IndianRupee className={`w-3.5 h-3.5 ${isLow ? 'text-red-700' : 'text-gray-900'}`} />
            <span className={`text-base font-bold leading-none ${isLow ? 'text-red-700' : 'text-gray-900'}`}>
              {formatCurrency(balance)}
            </span>
          </div>
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 ml-1 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {/* Expanded dropdown */}
      {expanded && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setExpanded(false)} />
          <div className="absolute right-0 top-full mt-2 z-20 w-72 bg-white border border-gray-200 rounded-2xl shadow-xl overflow-hidden">
            {/* Header */}
            <div className="px-5 pt-5 pb-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-1.5">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Wallet Balance</span>
                  <div className="relative group/rates">
                    <Info className="w-3.5 h-3.5 text-gray-400 cursor-pointer hover:text-gray-600 transition-colors" />
                    <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 w-52 bg-white border border-gray-200 rounded-xl shadow-lg py-3 px-4 opacity-0 invisible group-hover/rates:opacity-100 group-hover/rates:visible transition-all duration-200 z-30">
                      <p className="text-[10px] text-gray-500 uppercase font-medium tracking-wider mb-2">Our Rates</p>
                      <div className="space-y-2.5">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Phone className="w-3 h-3 text-blue-500" />
                            <span className="text-xs text-gray-500">AI Call</span>
                          </div>
                          <span className="text-xs font-semibold text-gray-900">₹{data?.aiCallRate ?? 2}/30 sec</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Smartphone className="w-3 h-3 text-green-500" />
                            <span className="text-xs text-gray-500">Phone Rental</span>
                          </div>
                          <span className="text-xs font-semibold text-gray-900">₹{data?.phoneRentalRate ?? 1000}/no.</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <MessageSquare className="w-3 h-3 text-purple-500" />
                            <span className="text-xs text-gray-500">Message</span>
                          </div>
                          <span className="text-xs font-semibold text-gray-900">₹{data?.messageRate ?? 1}/msg</span>
                        </div>
                      </div>
                      <span className="absolute bottom-full left-1/2 -translate-x-1/2 border-[5px] border-transparent border-b-white" />
                    </div>
                  </div>
                </div>
                {isLow && (
                  <span className="text-[10px] font-semibold text-red-600 bg-red-50 px-2 py-0.5 rounded-full border border-red-100">
                    Low Balance
                  </span>
                )}
              </div>
              <div className="flex items-baseline gap-1">
                <IndianRupee className="w-5 h-5 text-gray-900" />
                <span className="text-3xl font-bold text-gray-900 tracking-tight">{formatCurrency(balance)}</span>
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-gray-100" />

            {/* Usage breakdown */}
            <div className="px-5 py-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">This Month</span>
                <span className="text-xs text-gray-400">
                  Total: <span className="font-medium text-gray-600">{formatCurrency(totalSpent)}</span>
                </span>
              </div>
              <div className="space-y-2.5">
                {USAGE_ITEMS.map(({ key, label, icon: ItemIcon, color, bg }) => (
                  <div key={key} className="flex items-center gap-3">
                    <div className={`w-7 h-7 rounded-md flex items-center justify-center ${bg} flex-shrink-0`}>
                      <ItemIcon className={`w-3.5 h-3.5 ${color}`} />
                    </div>
                    <span className="text-sm text-gray-700 flex-1">{label}</span>
                    <div className="flex items-center gap-0.5 text-sm font-medium text-gray-900">
                      <IndianRupee className="w-3 h-3" />
                      {formatCurrency(usage[key])}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-100 px-5 py-3">
              <button
                onClick={() => { setExpanded(false); navigate('/settings?tab=billing'); }}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-[#2F55FF] text-white text-sm font-medium rounded-xl hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Funds
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MinutesBalanceWidget;
